import type { LandmarkDef } from './common';
import { landmarkModel, windowGlass } from './model';

/**
 * landmarks/lagos.ts — Lagos's landmarks, all hand-built low-poly Blender models
 * (glTF). The Lekki-Ikoyi cable bridge is a drive-through ('span') scaled by width.
 *
 * GS-Lagos Blender round (2026-08-10) — all four rebuilt from scratch as
 * scripted geometry (the London-round precedent: a script beats a scan on both
 * quality and size). MATERIAL NAMES ARE LOAD-BEARING: texturing.ts classifies by
 * name, and the old models lost every triplanar role to the SKIP regex —
 * 'white', 'red', 'rock', 'dark' and 'steel' are all SKIP tokens, which is why
 * QA saw dead-flat untextured bands next to textured ones. The rebuilds route
 * masonry through /conc/ (CONCRETE) and /stone/ (STONE) instead. Anything that
 * must read PALE uses CONCRETE, never PLASTER — plaster is the darkest, warmest
 * atlas and no tint can lift it (the pale-read law).
 *
 * SIDE-PLACEMENT FOOTPRINTS: `lateral = halfWidth + offset`, so a wide model at
 * a small offset overhangs the guardrail. Measured at hw=18 on the exported
 * GLBs — lighthouse +24.2u clear, cocoaHouse +3.0u, nationalTheatre +5.1u
 * (the OLD theatre overhung the road edge by 2.9u; hence offset 18 -> 24).
 */
export const LAGOS_LANDMARKS: Record<string, LandmarkDef> = {
  // Gallery was a thin torus FLOATING around the tower neck, the lantern a bare
  // drum, the base a white pancake. Now: welded gallery deck on corbel brackets
  // with a real balustrade, a glazed lamp room with an emissive lamp behind
  // astragal bars, and a faceted dark-reef island with a foam wash ring.
  lagoonLighthouse: { build: () => landmarkModel('models/landmarks/lagoon_lighthouse.glb', { height: 26 }), placement: 'side', offset: 34 },
  // GS6: warm-afternoon city. NT_win was ONE blown-out white emissive stripe
  // wrapping the whole drum (a fluorescent-tube collar). GS-Lagos re-cut it into
  // mullioned window STRIPS (36 piers), gave the cap roof radial ridges by
  // fluting alternate azimuths, and merged the 5 sticks that pierced the roof
  // into 8 triangular gusset buttresses that lie ON the roof slope and die into
  // the crown collar. Offset 18 -> 24: see the footprint note above.
  nationalTheatre: { build: () => landmarkModel('models/landmarks/national_theatre.glb', { height: 24, ...windowGlass('NT_win', 0.55) }), placement: 'side', offset: 24 },
  // Was a featureless putty-grey slab — less detailed than the filler towers
  // beside it. Rebuilt as the real Cocoa House: vertical concrete fins over a
  // recessed glazing core with floor spandrels, solid end walls, podium +
  // canopy, stepped crown and a rooftop COCOA HOUSE board (3D text, the M43
  // pipeline: FONT curve -> mesh, rotx=pi/2 faces -Y).
  cocoaHouse: { build: () => landmarkModel('models/landmarks/cocoa_house.glb', { height: 52, ...windowGlass('win', 0.6) }), placement: 'side', offset: 12 },
  // GS-Lagos: rebuilt from scratch (scripted geometry, the London-round
  // precedent). The old GLB ended its deck beam in MID-AIR over the grass and
  // its cable fans terminated as free-floating wireframe spokes lying ON the
  // road — at the finish line, the last thing a player sees. Now: a real
  // cable-stayed span — inverted-Y pylon (splayed legs, pier collar at the
  // waterline, single leaning mast), cables landing on actual deck-edge ANCHOR
  // BLOCKS in two planes, and the deck landing on stepped battered abutments
  // with wing walls at BOTH ends instead of stopping in space.
  //
  // GATEWAY CLEARANCE (the Tower Bridge / gatewayOfIndia precedent — a
  // ROAD-RELATIVE width, never a fixed scale). MEASURED on the exported GLB:
  // the narrowest obstruction below the deck soffit is the left abutment face
  // at x -25.5 against the pylon pier face at x +24.5, so the symmetric clear
  // opening is 49.0 of the model's 70.0 total X width = exactly 70.0%. Hence
  // width = (road width + 8) / 0.70 leaves ~4u of clear air outside each
  // guardrail at ANY track half-width. At hw=18: 62.9 wide, 42.1 tall, opening
  // ±22 over a ±18 road, deck soffit ~13.3 up (chase cam rides ~4.8).
  //
  // NOTE the old file shipped TWO scenes — the real one plus an empty 'Scene'
  // with no nodes — which three.js ignored (it reads scene 0) but which made
  // the GLB UNIMPORTABLE in Blender (importer iterates scene.nodes → None).
  // This export is single-scene; keep it that way.
  lekkiIkoyi: {
    build: (hw) => landmarkModel('models/landmarks/lekki_ikoyi.glb', { width: (2 * hw + 8) / 0.7 }),
    placement: 'span',
  },
};
